/**
 * V2 admin — the ranger station (V1-PARITY M10, pulled pre-flip by user
 * decision 2026-07-08). Route: /adventure/admin[/:tab].
 *
 * The shell owns the role gate, the tab cards (with live counts), and
 * the emergency read-only toggle; each tab is its own mini-view module
 * with the standard mount(el, ctx) → cleanup contract. The URL carries
 * the active tab, so tabs are shareable and survive reload.
 */
import * as gateway from '../../core/gateway.js';
import { invalidate } from '../../core/query.js';
import { showToast } from '../../ui/toast.js';
import { loadOverview, getOverview, resetOverview } from './data.js';
import * as dashboard from './dashboard.js';
import * as users from './users.js';
import * as categories from './categories.js';
import * as personal from './personal.js';
import * as yoinks from './yoinks.js';
import * as locations from './locations.js';
import * as lists from './lists.js';
import * as traffic from './traffic.js';
import * as marketing from './marketing.js';
import * as settings from './settings.js';
import * as feedback from './feedback.js';

const num = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

export const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊', view: dashboard },
  // Second on purpose: the creator campaign is daily work with a due date
  // on it (docs/CREATOR-EMBED-CAMPAIGN.md), and its badge counts follow-ups
  // owed today — a thing that has to be in the way to get done.
  { id: 'marketing', label: 'Marketing', icon: '📣', view: marketing },
  { id: 'yoinks', label: 'Yoinks', icon: '📸', view: yoinks },
  { id: 'users', label: 'Explorers', icon: '🧑‍🤝‍🧑', view: users },
  { id: 'locations', label: 'Locations', icon: '📍', view: locations },
  { id: 'lists', label: 'Lists', icon: '🔖', view: lists },
  { id: 'categories', label: 'Categories', icon: '📁', view: categories },
  // Beside Categories, not inside it: these are explorers' own shelves,
  // never admin-curated (docs/JOURNAL-PLAN.md §11)
  { id: 'personal', label: 'Personal', icon: '🔒', view: personal },
  { id: 'traffic', label: 'Traffic', icon: '🚦', view: traffic },
  // Messages from the footer's Tell Us box. Badged with the open count
  // for the same reason Marketing is: a queue nobody is nagged about is
  // a queue nobody works.
  { id: 'feedback', label: 'Feedback', icon: '💬', view: feedback },
  { id: 'settings', label: 'Settings', icon: '⚙️', view: settings },
];

function tabCount(overview, id) {
  if (!overview) return '';
  const value = {
    dashboard: `${num(overview.todayYoinks)} today`,
    yoinks: num(overview.totalYoinks),
    users: `${num(overview.totalUsers)} · ${num(overview.loggedInToday)} today`,
    locations: num(overview.totalLocations),
    categories: num(overview.totalCategories),
    // Only when there IS something owed — a badge that always reads 0 stops
    // being read at all. Undefined (pre-migration) shows nothing.
    marketing: overview.marketingDue ? `${num(overview.marketingDue)} due` : '',
    feedback: overview.feedbackOpen ? `${num(overview.feedbackOpen)} open` : '',
  }[id];
  return value ? `<span class="admin-tab-count">${value}</span>` : '';
}

export function mount(el, ctx) {
  const { store, router, params } = ctx;
  let destroyed = false;
  let sectionCleanup = null;
  let settingsRow = null; // app_settings row (id + is_read_only) for the toggle

  const tab = TABS.find((t) => t.id === params?.tab) || TABS[0];

  function gateHtml(state) {
    if (!state.authReady) return '<div class="v2-loading">Checking your ranger badge…</div>';
    if (!state.user) return `
      <div class="v2-placeholder">
        <h2>Rangers only</h2>
        <p>Sign in with an admin account to open the ranger station.</p>
      </div>`;
    if (state.profile?.role !== 'admin') return `
      <div class="v2-placeholder">
        <h2>Rangers only</h2>
        <p>This area is for park staff. The trails are that way →</p>
        <a href="${window.YOINK_CONFIG?.basePath || '/adventure'}/" data-route="discover">← Back to Discover</a>
      </div>`;
    return null;
  }

  function emergencyBarHtml() {
    const isReadOnly = Boolean(settingsRow?.is_read_only);
    return `
      <div class="admin-emergency ${isReadOnly ? 'admin-emergency-on' : ''}" data-emergency-bar>
        <div>
          <strong>${isReadOnly ? '🔒 Read-only mode is ON' : '🔓 App is live'}</strong>
          <span class="admin-emergency-desc">${isReadOnly
            ? 'Explorers can browse but not create, edit, or delete.'
            : 'Explorers can create and edit content.'}</span>
        </div>
        <button type="button" class="admin-emergency-btn" data-emergency-toggle
          ${settingsRow ? '' : 'disabled'}>
          ${isReadOnly ? 'Disable read-only' : 'Enable read-only'}</button>
      </div>`;
  }

  function shellHtml() {
    const overview = getOverview();
    return `
      <div class="admin-shell">
        <header class="admin-head">
          <div>
            <h2 class="admin-heading">Ranger station</h2>
            <p class="admin-subhead">Everything behind the counter — handle with care.</p>
          </div>
          <button type="button" class="admin-refresh" data-admin-refresh>↻ Refresh</button>
        </header>
        ${emergencyBarHtml()}
        <nav class="admin-tabs" aria-label="Admin sections">
          ${TABS.map((t) => `
            <a class="admin-tab ${t.id === tab.id ? 'is-active' : ''}"
              href="${router.generate('admin', { tab: t.id })}" data-admin-tab="${t.id}"
              ${t.id === tab.id ? 'aria-current="page"' : ''}>
              <span class="admin-tab-icon">${t.icon}</span>
              <span class="admin-tab-label">${t.label}</span>
              ${tabCount(overview, t.id)}
            </a>`).join('')}
        </nav>
        <div class="admin-content" data-admin-content></div>
      </div>`;
  }

  function refreshTabCounts() {
    const overview = getOverview();
    if (!overview) return;
    el.querySelectorAll('[data-admin-tab]').forEach((tabEl) => {
      const count = tabCount(overview, tabEl.dataset.adminTab);
      const existing = tabEl.querySelector('.admin-tab-count');
      if (existing) existing.outerHTML = count;
      else tabEl.insertAdjacentHTML('beforeend', count);
    });
  }

  function mountSection() {
    if (sectionCleanup) sectionCleanup();
    const content = el.querySelector('[data-admin-content]');
    sectionCleanup = tab.view.mount(content, { store, router });
  }

  async function loadSettingsRow() {
    const { data } = await gateway.rawQuery(gateway.TABLES.APP_SETTINGS, {
      select: 'id,is_read_only', limit: 1,
    });
    settingsRow = data?.[0] || null;
    if (destroyed) return;
    const bar = el.querySelector('[data-emergency-bar]');
    if (bar) bar.outerHTML = emergencyBarHtml();
  }

  async function toggleReadOnly() {
    if (!settingsRow) return;
    const btn = el.querySelector('[data-emergency-toggle]');
    if (btn) btn.disabled = true;
    const newValue = !settingsRow.is_read_only;
    const { error } = await gateway.rawUpdate(gateway.TABLES.APP_SETTINGS, {
      filters: { id: `eq.${settingsRow.id}` },
      body: { is_read_only: newValue, updated_at: new Date().toISOString() },
    });
    if (destroyed) return;
    if (error) {
      showToast('Couldn’t flip read-only mode.', { type: 'info' });
      if (btn) btn.disabled = false;
      return;
    }
    settingsRow.is_read_only = newValue;
    invalidate('app-settings'); // next loadAppSettings sees the truth
    store.set({ isReadOnly: newValue });
    const bar = el.querySelector('[data-emergency-bar]');
    if (bar) bar.outerHTML = emergencyBarHtml();
    showToast(`Read-only mode ${newValue ? 'enabled' : 'disabled'}.`, { type: 'success' });
  }

  function onClick(event) {
    const tabEl = event.target.closest('[data-admin-tab]');
    if (tabEl) {
      event.preventDefault();
      router.navigate('admin', { tab: tabEl.dataset.adminTab });
      return;
    }
    if (event.target.closest('[data-emergency-toggle]')) {
      toggleReadOnly();
      return;
    }
    if (event.target.closest('[data-admin-refresh]')) {
      resetOverview();
      loadOverview().then(() => { if (!destroyed) refreshTabCounts(); });
      mountSection();
    }
  }

  let shellUp = false; // a local flag, not DOM sniffing — the router
  // remounts this view with the previous mount's markup still in el

  function render() {
    const gate = gateHtml(store.get());
    if (gate) {
      if (sectionCleanup) { sectionCleanup(); sectionCleanup = null; }
      shellUp = false;
      el.innerHTML = gate;
      return;
    }
    if (shellUp) return;
    shellUp = true;
    el.innerHTML = shellHtml();
    mountSection();
    loadSettingsRow();
    if (!getOverview()) {
      loadOverview().then(() => { if (!destroyed) refreshTabCounts(); });
    }
  }

  render();
  // The auth slice settles late on cold loads — re-check the gate when it does
  const unsubscribe = store.subscribe(() => { if (!destroyed) render(); });
  el.addEventListener('click', onClick);

  return () => {
    destroyed = true;
    unsubscribe();
    el.removeEventListener('click', onClick);
    if (sectionCleanup) sectionCleanup();
  };
}
